import { StaticImage } from "gatsby-plugin-image"
import React from "react"
import * as styles from "./index.module.css"
import HeadingCover from "../../../components/text/heading-cover/heading-cover"
import BodyText from "../../../components/text/body-text/body-text"
import CardsCarousel from "../../../components/cards-carousel/cards-carousel"
import CourseCard from "../../../components/CourseCard/CourseCard"
import Button from "../../../components/button/button"
import { graphql, useStaticQuery, navigate } from "gatsby"
import { LazyLoadImage } from "react-lazy-load-image-component"

const commonImageProps = {
  layout: "constrained",
  placeholder: "blurred",
  formats: ["auto", "webp"],
  outputPixelDensities: [0.5, 0.75, 1, 2],
}

export const JobLinkedCourses = () => {
  const data = useStaticQuery(graphql`
    query {
      allSector {
        nodes {
          id
          sectorId
          name
          primaryImage
          whyJoin
          createdOn
          modifiedOn
          order
          showOnHome
          employersList {
            image
            text
          }
        }
      }
      allCourse {
        nodes {
          id
          courseId
          sectorId
          name
          description
          type
          learningMode
          duration
          tag
          isBestPlacement
          placementSupportPercent
          eligibility
          startingSalary
          scope
          whyJoinFromSector
          whyJoin
          whatYouWillDo
          trainingHighlights
          trainingHighlightImage
          includeEmployersFromSectors
          certificateImage
          courseImage
          isDeleted
          slug
          showOnHome
          createdOn
          modifiedOn
          order
          metaTitle
          metaDescription
          metaKeywords
          hideTrainingJourney
          employersList {
            image
            text
          }
          certifications
          learnings {
            domainKnowledge {
              type
              text
              headings
            }
            lifeSkill {
              type
              text
              headings
            }
            onTheJob {
              type
              text
              headings
            }
            interviewPreparation {
              type
              text
              headings
            }
          }
          testimonials {
            name
            department
            companyName
            text
          }
          hasCustomDuration
          duration2
          hasCustomLearnings
          customLearning {
            heading
            image
            item {
              type
              text
              headings
            }
          }
        }
      }
    }
  `)
  const sectors = React.useMemo(() => {
    const tempSectors = {}
    
    data.allSector?.nodes?.forEach(sector => {
      if(sector.showOnHome){
        tempSectors[sector.sectorId] = { name: sector.name, sectorId: sector.sectorId, courses: [] }
      }
    })

    tempSectors["Others"] = {
      name: "Others", sectorId: "Others", courses: []
    }

    data.allCourse?.nodes?.forEach(course => {
      if(course.showOnHome){
        if (tempSectors[course.sectorId]) {
          tempSectors[course.sectorId].courses.push(course)
        } else {
          tempSectors["Others"].courses.push(course)
        }
      }
    })
  
    return tempSectors
  }, [data]) // Recalculate only when `data` changes
  
  const filteredSectors = React.useMemo(() => 
    Object.values(sectors)
      .filter(val => val.courses?.length)
      .sort((a, b) => a.order - b.order),
  [sectors])
  
  const homeCourseList2 = React.useMemo(() => 
    filteredSectors.map(sector => ({
      name: sector.name === "Other" ? "Others" : sector.name,
      sectorId: sector.sectorId,
      courses: sector.courses.map(course => ({
        name: course.name,
        learningMode: course.learningMode,
        tag: course.tag,
        duration: course.duration,
        graduationType: course.type,
        image: <LazyLoadImage style={{aspectRatio: 1.6, objectFit: 'cover'}} alt={"Image " + course.name} src={course.courseImage} />,
        slug: course.slug,
        showOnHome: course.showOnHome,
        order: course.order
      })).sort((a,b)=>a?.order-b?.order),
    })),
  [filteredSectors])
  // console.log(filteredSectors);

  const [selectedHomeCourseIndex, setSelectedHomeCourseIndex] =
    React.useState(0)

  return (
    <>
      <HeadingCover className="px-4 text-center mb-2 mx-auto">
        <span>Best Job-Linked</span> Courses In <span>One Place</span>
      </HeadingCover>
      <div className="container mx-auto my-3">
        <div className="flex gap-3 items-center justify-center">
          {homeCourseList2.map((val, index) => (
            <div
              className={
                selectedHomeCourseIndex == index
                  ? styles.activeCourseTab
                  : styles.courseTab
              }
              key={val.name}
              onClick={() => setSelectedHomeCourseIndex(index)}
            >
              <BodyText variant="bodyText7">{val.name}</BodyText>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-[#D1D7DC] bg-[#F7F9FA]  py-6">
        <div className="container mx-auto">
          <CardsCarousel
            carouselSettingsOverride={{
              key: selectedHomeCourseIndex,
              slidesToScroll: 1,
              slidesToShow: 4,
              autoplay: true,
              autoplaySpeed: 5000,
              infinite:
                (homeCourseList2[selectedHomeCourseIndex]?.courses?.length ||
                  0) > 4,
              dots:
                (homeCourseList2[selectedHomeCourseIndex]?.courses?.length ||
                  0) > 4,
              responsive: [
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                    dots:
                      (homeCourseList2[selectedHomeCourseIndex]?.courses
                        ?.length || 0) > 3,
                    infinite:
                      (homeCourseList2[selectedHomeCourseIndex]?.courses
                        ?.length || 0) > 3,
                  },
                },
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    dots:
                      (homeCourseList2[selectedHomeCourseIndex]?.courses
                        ?.length || 0) > 2,
                    infinite:
                      (homeCourseList2[selectedHomeCourseIndex]?.courses
                        ?.length || 0) > 2,
                  },
                },
                {
                  breakpoint: 480,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    dots:
                      (homeCourseList2[selectedHomeCourseIndex]?.courses
                        ?.length || 0) > 1,
                    infinite:
                      (homeCourseList2[selectedHomeCourseIndex]?.courses
                        ?.length || 0) > 1,
                  },
                },
              ],
            }}
          >
            {homeCourseList2[selectedHomeCourseIndex]?.courses?.map(
              (course, ind) => (
                <div key={course.name + "_" + ind} className="px-3 h-full cursor-pointer" onClick={()=>{
                  navigate(`/course/${course.slug}`)
                }}>
                  <CourseCard
                    img={course.image}
                    title={course.name}
                    offlineText={course.learningMode}
                    duration={course.duration}
                    certificationCount={course.certificationCount}
                    tag={course.tag}
                    graduationType={course.graduationType}
                  />
                </div>
              )
            )}
          </CardsCarousel>
        </div>
        <div className="text-center my-3 mx-auto">
          <Button className="mb-0" onClick={()=>{
            navigate('/courses')
          }}>Know More</Button>
        </div>
      </div>
    </>
  )
}
